import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend } from "recharts";
import { IdentificationCard, DownloadSimple, FilePdf, FileXls, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function downloadWithAuth(path, filename) {
  const res = await fetch(`${BACKEND_URL}/api${path}`, { credentials: "include" });
  if (!res.ok) throw new Error("Gagal mengunduh");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export default function SantriDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [charts, setCharts] = useState(null);
  const [tab, setTab] = useState("identitas");

  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          api.get(`/laporan/santri/${id}`),
          api.get(`/dashboard/santri/${id}/charts`),
        ]);
        setData(r1.data);
        setCharts(r2.data);
      } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    })();
  }, [id]);

  if (!data) return <div className="text-slate-500">Memuat...</div>;
  const s = data.santri;

  const doPdf = () => downloadWithAuth(`/laporan/santri/${id}/pdf`, `laporan_${s.nis}.pdf`).catch((e) => toast.error(e.message));
  const doXlsx = () => downloadWithAuth(`/laporan/santri/${id}/xlsx`, `laporan_${s.nis}.xlsx`).catch((e) => toast.error(e.message));

  const tabs = [
    { key: "identitas", label: "Identitas" },
    { key: "grafik", label: "Grafik" },
    { key: "absensi", label: `Absensi (${data.absensi.length})` },
    { key: "perizinan", label: `Perizinan (${data.perizinan.length})` },
    { key: "pelanggaran", label: `Pelanggaran (${data.pelanggaran.length})` },
    { key: "tahfiz", label: `Tahfiz (${data.tahfiz.length})` },
    { key: "kegiatan", label: `Kegiatan (${data.kegiatan.length})` },
    { key: "makan", label: `Makan (${data.makan.length})` },
    { key: "riwayat", label: `Riwayat Perubahan (${data.history?.length || 0})` },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/santri" className="imsyaf-btn-ghost"><ArrowLeft size={16} /></Link>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400">Detail Santri</div>
            <h1 className="font-display text-3xl font-bold text-[#064E3B]">{s.nama}</h1>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/santri/${id}/id-card`} className="imsyaf-btn-outline" data-testid="detail-id-card"><IdentificationCard size={16} /> Kartu ID</Link>
          <button onClick={doPdf} className="imsyaf-btn-outline" data-testid="detail-download-pdf"><FilePdf size={16} /> PDF</button>
          <button onClick={doXlsx} className="imsyaf-btn-outline" data-testid="detail-download-xlsx"><FileXls size={16} /> Excel</button>
        </div>
      </div>

      <div className="imsyaf-card p-4 flex flex-wrap gap-1">
        {tabs.map((t) => (
          <button
            key={t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === t.key ? "bg-[#14532d] text-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "identitas" && (
        <div className="imsyaf-card p-6 grid md:grid-cols-2 gap-4 text-sm">
          {[
            ["NIS", s.nis], ["Nama", s.nama], ["Jenis Kelamin", s.jenis_kelamin === "L" ? "Laki-laki" : "Perempuan"],
            ["TTL", `${s.tempat_lahir || "-"}, ${s.tanggal_lahir || "-"}`], ["Alamat", s.alamat || "-"],
            ["Kelas", s.kelas_nama || "-"], ["Kamar", s.kamar_nama || "-"], ["Halaqah", s.halaqah_nama || "-"],
            ["Nama Wali", s.nama_wali || "-"], ["No HP Wali", s.no_hp_wali || "-"],
            ["Status", s.status], ["Tanggal Masuk", s.tanggal_masuk || "-"],
            ["Total Poin Pelanggaran", <span className="font-bold text-red-700">{data.total_poin}</span>],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-slate-100 pb-2">
              <span className="text-slate-500">{k}</span><span className="font-medium text-right">{v}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "grafik" && charts && (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="imsyaf-card p-5">
            <h3 className="font-display text-lg font-semibold text-[#064E3B] mb-3">Perkembangan Hafalan (Nilai)</h3>
            <div className="h-64">
              <ResponsiveContainer><LineChart data={charts.hafalan}><CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" /><XAxis dataKey="tanggal" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} domain={[0, 100]} /><Tooltip /><Line type="monotone" dataKey="nilai" stroke="#14532d" strokeWidth={2} /></LineChart></ResponsiveContainer>
            </div>
          </div>
          <div className="imsyaf-card p-5">
            <h3 className="font-display text-lg font-semibold text-[#064E3B] mb-3">Kehadiran 30 Hari</h3>
            <div className="h-64">
              <ResponsiveContainer><BarChart data={charts.kehadiran}><CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" /><XAxis dataKey="tanggal" hide /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="hadir" fill="#4ade80" /><Bar dataKey="total" fill="#166534" /></BarChart></ResponsiveContainer>
            </div>
          </div>
          <div className="imsyaf-card p-5 lg:col-span-2">
            <h3 className="font-display text-lg font-semibold text-[#064E3B] mb-3">Kedisiplinan (Poin Pelanggaran / Bulan)</h3>
            <div className="h-56">
              <ResponsiveContainer><BarChart data={charts.kedisiplinan}><CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" /><XAxis dataKey="bulan" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="poin" fill="#dc2626" /></BarChart></ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {tab === "absensi" && <SimpleList rows={data.absensi} cols={[["tanggal","Tanggal"],["tipe","Tipe"],["status","Status"],["jam","Jam"],["keterangan","Keterangan"]]} />}
      {tab === "perizinan" && <SimpleList rows={data.perizinan} cols={[["tanggal_izin","Tanggal"],["jenis_nama","Jenis"],["status","Status"],["alasan","Alasan"],["penjemput_nama","Penjemput"]]} />}
      {tab === "pelanggaran" && <SimpleList rows={data.pelanggaran} cols={[["tanggal","Tanggal"],["jenis_nama","Jenis"],["poin","Poin"],["keterangan","Ket"]]} />}
      {tab === "tahfiz" && <SimpleList rows={data.tahfiz} cols={[["tanggal","Tanggal"],["tipe","Tipe"],["surat","Surat"],["ayat_dari","Ayat"],["nilai_total","Nilai"],["predikat","Predikat"]]} />}
      {tab === "kegiatan" && <SimpleList rows={data.kegiatan} cols={[["tanggal","Tanggal"],["kegiatan_nama","Kegiatan"],["status","Status"]]} />}
      {tab === "makan" && <SimpleList rows={data.makan} cols={[["tanggal","Tanggal"],["sesi","Sesi"],["status","Status"]]} />}
      {tab === "riwayat" && <HistoryList rows={data.history || []} />}
    </div>
  );
}

const SimpleList = ({ rows, cols }) => (
  <div className="imsyaf-card overflow-hidden">
    <table className="w-full text-sm zebra">
      <thead className="bg-slate-50 text-slate-700"><tr>{cols.map(([, l]) => <th key={l} className="text-left px-4 py-2 font-semibold">{l}</th>)}</tr></thead>
      <tbody>{rows.length === 0 ? (
        <tr><td className="p-6 text-center text-slate-400" colSpan={cols.length}>Belum ada data.</td></tr>
      ) : rows.map((r, i) => (
        <tr key={i} className="border-t border-slate-100">{cols.map(([k]) => <td key={k} className="px-4 py-2">{r[k] ?? "—"}</td>)}</tr>
      ))}</tbody>
    </table>
  </div>
);

const HistoryList = ({ rows }) => (
  <div className="imsyaf-card p-5 space-y-3">
    {rows.length === 0 && <p className="text-slate-400 text-sm">Belum ada perubahan.</p>}
    {rows.map((r) => (
      <div key={r.id} className="border-l-4 border-green-700 pl-3">
        <div className="text-xs text-slate-500">{r.created_at} — {r.actor_name || "—"}</div>
        <div className="text-sm">
          {Object.entries(r.changes || {}).map(([k, v]) => (
            <div key={k}><b>{k}</b>: <span className="line-through text-red-600 mr-1">{String(v.before ?? "—")}</span> → <span className="text-green-700">{String(v.after ?? "—")}</span></div>
          ))}
        </div>
      </div>
    ))}
  </div>
);
